<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useDraggableLauncher } from 'dashboard/composables/useDraggableLauncher';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
const route = useRoute();

const { uiSettings, updateUISettings } = useUISettings();

const isConversationRoute = computed(() => {
  const CONVERSATION_ROUTES = [
    'inbox_conversation',
    'conversation_through_inbox',
    'conversations_through_folders',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_participating',
    'conversation_through_ai_handling',
    'conversation_through_resolved_by_ai',
    'inbox_view_conversation',
  ];
  return CONVERSATION_ROUTES.includes(route.name);
});

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotLauncher = computed(() => {
  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
  return (
    isCaptainEnabled &&
    !uiSettings.value.is_copilot_panel_open &&
    !isConversationRoute.value
  );
});

const launcherRef = ref(null);
const storedPosition = computed(
  () => uiSettings.value.copilot_launcher_position
);

const { position, isDragging, hasDragged, onPointerDown } =
  useDraggableLauncher({
    elementRef: launcherRef,
    initialPosition: storedPosition,
    onDragEnd: value => updateUISettings({ copilot_launcher_position: value }),
  });

const positionStyle = computed(() =>
  position.value
    ? { top: `${position.value.y}px`, left: `${position.value.x}px` }
    : {}
);

const toggleSidebar = () => {
  // A drag ends with a click event on the button, ignore it so the panel
  // does not open when the launcher is only being repositioned.
  if (hasDragged.value) return;
  updateUISettings({
    is_copilot_panel_open: !uiSettings.value.is_copilot_panel_open,
    is_contact_sidebar_open: false,
  });
};
</script>

<template>
  <div
    v-if="showCopilotLauncher"
    ref="launcherRef"
    class="fixed z-50 select-none touch-none"
    :class="[
      position ? '' : 'bottom-4 ltr:right-4 rtl:left-4',
      isDragging ? 'cursor-grabbing' : 'cursor-grab',
    ]"
    :style="positionStyle"
    @pointerdown="onPointerDown"
  >
    <ButtonGroup
      class="rounded-full bg-n-alpha-2 backdrop-blur-lg p-1 shadow hover:shadow-md"
    >
      <Button
        icon="i-woot-captain"
        no-animation
        class="!rounded-full !bg-n-solid-3 dark:!bg-n-alpha-2 !text-n-slate-12 text-xl transition-all duration-200 ease-out hover:brightness-110"
        lg
        @click="toggleSidebar"
      />
    </ButtonGroup>
  </div>
  <template v-else />
</template>
